export type UserRole = "superadmin" | "cliente_admin" | "gerente_clinica" | "dentista" | "recepcao" | "financeiro" | "estoque";

export const roleLabels: Record<UserRole, string> = {
  superadmin: "SuperAdmin",
  cliente_admin: "Administrador do Cliente",
  gerente_clinica: "Gerente da Clinica",
  dentista: "Dentista",
  recepcao: "Recepcao",
  financeiro: "Financeiro",
  estoque: "Estoque",
};

export const rolePermissions: Record<UserRole, string[]> = {
  superadmin: ["*"],
  cliente_admin: ["clientes:manage", "clinicas:manage", "usuarios:manage", "gestao:view", "agenda:manage", "agenda:delete", "agenda:feriados", "cadastros:manage", "financeiro:manage", "financeiro:view", "financeiro:totais", "recibos:emit", "relatorios:view", "atestados:manage", "contratos:manage", "sessoes:manage", "seguranca:mfa", "ia:manage", "campanhas:manage"],
  gerente_clinica: ["usuarios:view", "gestao:view", "agenda:manage", "agenda:delete", "agenda:feriados", "cadastros:manage", "financeiro:view", "financeiro:totais", "recibos:emit", "relatorios:view", "atestados:manage", "contratos:manage"],
  dentista: ["agenda:manage", "pacientes:view", "recibos:emit", "clinico:manage", "financeiro:view", "financeiro:totais", "relatorios:view", "atestados:manage", "contratos:manage"],
  recepcao: ["agenda:manage", "pacientes:manage", "recibos:emit", "cadastros:view", "financeiro:view", "financeiro:totais", "relatorios:view", "atestados:manage", "contratos:manage"],
  financeiro: ["agenda:manage", "pacientes:view", "recibos:emit", "financeiro:manage", "financeiro:view", "financeiro:totais", "relatorios:view", "atestados:manage", "contratos:manage"],
  estoque: ["agenda:manage", "pacientes:view", "recibos:emit", "estoque:manage", "fornecedores:view", "financeiro:view", "financeiro:totais", "relatorios:view", "atestados:manage", "contratos:manage"],
};

export const delegablePermissions: ReadonlyArray<{ key: string; label: string; description: string; modulo?: string }> = [
  { key: "gestao:view", label: "Central de gestão", description: "Integridade da unidade, fechamento, conciliação e auditoria.", modulo: undefined },
  { key: "agenda:manage", label: "Agenda", description: "Criar, editar e acompanhar agendamentos." },
  { key: "agenda:delete", label: "Agenda - excluir", description: "Excluir agendamentos. Use apenas para perfis de confiança." },
  { key: "agenda:feriados", label: "Feriados e bloqueios", description: "Decidir abertura em feriados e criar bloqueios da agenda por unidade." },
  { key: "pacientes:manage", label: "Pacientes", description: "Cadastrar pacientes, ver ficha e atualizar dados." },
  { key: "recibos:emit", label: "Emitir recibos", description: "Emitir recibos e registrar o recebimento no fluxo da unidade." },
  { key: "fornecedores:manage", label: "Fornecedores", description: "Cadastrar, editar e consultar fornecedores da clínica." },
  { key: "ordem_servico:manage", label: "Ordem de serviço", description: "Criar, editar e acompanhar ordens de serviço." },
  { key: "atestados:manage", label: "Atestados", description: "Preencher, emitir e administrar atestados.", modulo: "documentos_atestados" },
  { key: "contratos:manage", label: "Contratos", description: "Criar, editar, assinar e acompanhar contratos.", modulo: "documentos_atestados" },
  { key: "cadastros:view", label: "Cadastros - visualizar", description: "Ver cadastros auxiliares sem acesso financeiro gerencial." },
  { key: "cadastros:manage", label: "Cadastros - gerenciar", description: "Criar e editar cadastros operacionais da clínica." },
  { key: "clinico:manage", label: "Clínico", description: "Planos de tratamento, odontograma e evolução clínica." },
  { key: "estoque:manage", label: "Estoque", description: "Gerenciar materiais, estoque, fornecedores e laboratório.", modulo: "estoque_laboratorio" },
  { key: "financeiro:view", label: "Financeiro - consulta", description: "Consultar contas, cobranças e fluxo financeiro.", modulo: "controle_pagamentos" },
  { key: "financeiro:manage", label: "Financeiro - gerenciar", description: "Criar lançamentos, pagamentos e ações financeiras.", modulo: "controle_pagamentos" },
  { key: "financeiro:totais", label: "Totais financeiros", description: "Ver totais de vendas, fluxo de caixa e indicadores sensíveis.", modulo: "relatorios_avancados" },
  { key: "relatorios:view", label: "Relatórios gerenciais", description: "Ver relatórios, metas, comissões e indicadores.", modulo: "relatorios_avancados" },
  { key: "ia:manage", label: "Atendimento IA", description: "Configurar IA, WhatsApp, lembretes e base de conhecimento.", modulo: "atendimento_ia_whatsapp" },
  { key: "campanhas:manage", label: "Campanhas da rede", description: "Criar comunicações somente para pacientes das unidades liberadas.", modulo: "atendimento_ia_whatsapp" },
  { key: "sessoes:manage", label: "Sessões ativas", description: "Visualizar e encerrar sessões de usuários da conta." },
  { key: "seguranca:mfa", label: "Configurar MFA", description: "Ativar ou exigir segundo fator para administradores." },
];

export const recepcaoOperacionalPermissions = [
  "agenda:manage",
  "pacientes:manage",
  "recibos:emit",
  "financeiro:view",
  "fornecedores:manage",
  "ordem_servico:manage",
  "atestados:manage",
  "contratos:manage",
  "cadastros:view",
] as const;

export const permissionPresets: Record<string, string[]> = {
  recepcao: ["agenda:manage", "pacientes:manage", "recibos:emit", "atestados:manage", "contratos:manage", "cadastros:view"],
  dentista: ["agenda:manage", "pacientes:view", "clinico:manage", "recibos:emit", "atestados:manage", "contratos:manage"],
  financeiro: ["financeiro:view", "financeiro:manage", "financeiro:totais", "relatorios:view", "recibos:emit"],
  gerencia: ["gestao:view", "agenda:manage", "agenda:delete", "agenda:feriados", "cadastros:manage", "financeiro:view", "financeiro:totais", "relatorios:view"],
};

export const delegablePermissionKeys = delegablePermissions.map((permission) => permission.key);

export const mandatoryClinicPermissions = [
  "recibos:emit",
  "atestados:manage",
  "contratos:manage",
] as const;

export function sanitizeExtraPermissions(permissions: unknown) {
  const sanitized = Array.isArray(permissions) ? permissions
    .filter((permission): permission is string => typeof permission === "string")
    .filter((permission) => delegablePermissionKeys.includes(permission as typeof delegablePermissionKeys[number])) : [];
  return Array.from(new Set([...mandatoryClinicPermissions, ...sanitized]));
}

export function hasPermission(role: string | undefined, permission: string, extraPermissions: string[] = []) {
  const normalized = (role || "cliente_admin") as UserRole;
  const permissions = rolePermissions[normalized] || [];
  return permissions.includes("*") || permissions.includes(permission) || sanitizeExtraPermissions(extraPermissions).includes(permission);
}

export function hasAnyPermission(role: string | undefined, permissionsToCheck: string[], extraPermissions: string[] = []) {
  return permissionsToCheck.some((permission) => hasPermission(role, permission, extraPermissions));
}

export function permissionsForActiveClinic(
  basePermissions: string[] = [],
  permissionsByClinic: Record<string, string[]> | undefined,
  activeClinicaId?: string
) {
  const scoped = activeClinicaId && permissionsByClinic ? permissionsByClinic[activeClinicaId] || [] : [];
  return sanitizeExtraPermissions([...basePermissions, ...scoped]);
}
